import React, { useState, useCallback } from "react";
import styled from "styled-components";
import { Eye, EyeOff, Info } from "lucide-react";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import { v4 as uuidv4 } from "uuid";
import { useNavigate } from "react-router-dom";

// Cognito-permitted special characters (top-level, all can access)
const cognitoSpecialChars = '^$*.[]{}()?"!@#%&/\\,><\':;|_~`=+-';



const Circumpunct = styled.div`
  position: relative;
  width: 96px;
  height: 96px;
  margin: 2rem auto;
  animation: spin 1s linear infinite;
  &::before {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: 24px;
    height: 24px;
    background: #002e5d;
    border-radius: 50%;
    transform: translate(-50%, -50%);
  }
  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 96px;
    height: 96px;
    border: 4px solid #002e5d;
    border-radius: 50%;
  }
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const Page = styled.div`
  min-height: 100vh;
  background-color: #f9fafb;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const Card = styled.div`
  background: white;
  padding: 2.5rem 2rem;
  max-width: 420px;
  width: 100%;
  border-radius: 12px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.05);
`;

const Heading = styled.h1`
  font-size: 1.8rem;
  font-weight: 600;
  text-align: center;
  margin-bottom: 1.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 2.5rem 0.75rem 0.9rem;
  border: 1px solid #ccc;
  border-radius: 8px;
  font-size: 0.95rem;
  &:focus {
    outline: none;
    border-color: #6366f1;
    box-shadow: 0 0 0 1px #6366f1;
  }
`;

const Button = styled.button`
  width: 100%;
  background-color: #002e5d;
  color: white;
  padding: 0.75rem;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  font-size: 0.95rem;
  cursor: pointer;
  transition: background 0.2s;
  &:hover {
    background-color: #001a3d;
  }
  &:disabled {
    background-color: #cbd5e1;
    cursor: not-allowed;
  }
`;

const PasswordPolicy = styled.div`
  background-color: #002e5d;
  color: white;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-size: 0.85rem;
  margin-top: 0.5rem;
  margin-bottom: 1rem;
  ul {
    padding-left: 1rem;
    margin-top: 0.25rem;
    margin-bottom: 0;
  }
  li {
    margin-bottom: 0.25rem;
    display: flex;
    align-items: center;
    font-size: 1em;
  }
`;

const TooltipWrapper = styled.span`
  position: relative;
  display: inline-block;
  margin-left: 6px;
`;
const TooltipText = styled.span`
  visibility: hidden;
  background-color: #1e293b;
  color: #fde68a;
  text-align: left;
  border-radius: 6px;
  padding: 0.4em 0.7em;
  position: absolute;
  z-index: 100;
  bottom: 130%;
  left: 50%;
  transform: translateX(-50%);
  opacity: 0;
  font-size: 0.95em;
  min-width: 210px;
  transition: opacity 0.2s;
  box-shadow: 0 2px 16px rgba(0,0,0,.16);
  &::after {
    content: '';
    position: absolute;
    top: 99%;
    left: 50%;
    margin-left: -7px;
    border-width: 7px;
    border-style: solid;
    border-color: #1e293b transparent transparent transparent;
  }
  ${TooltipWrapper}:hover & {
    visibility: visible;
    opacity: 1;
  }
`;

const StatusMessage = styled.p`
  margin-top: 1rem;
  color: ${(props) => (props.success ? "green" : "red")};
  font-size: 0.9rem;
  text-align: center;
`;

const InputGroup = styled.div`
  margin-bottom: 1rem;
`;

const PasswordWrapper = styled.div`
  position: relative;
  height: 43px;
`;
const PasswordToggleIcon = styled.div`
  position: absolute;
  right: 0.75rem;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  pointer-events: auto;
  cursor: pointer;
  color: #666;
  height: 100%;
`;
// Highlight password input green when valid
const PasswordInput = styled(Input)`
  border: 2px solid ${props => props.valid ? '#22c55e' : '#ccc'};
  color: ${props => props.valid ? '#22c55e' : 'black'};
`;

// Only Cognito permitted special chars. No space allowed. Reject any other symbol.
function checkPasswordRequirements(password, email) {
  const specialCharRegex = new RegExp('[' + cognitoSpecialChars.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&') + ']');
  const nonPermittedRegex = new RegExp(`[^a-zA-Z0-9${cognitoSpecialChars.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')}]`);
  const emailUser = email ? email.split('@')[0].toLowerCase() : '';
  return {
    length: password.length >= 8,
    upper: /[A-Z]/.test(password),
    lower: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    special: specialCharRegex.test(password),
    notEmail: emailUser ? !password.toLowerCase().includes(emailUser) : true,
    noNonPermitted: !nonPermittedRegex.test(password), // false if forbidden char present
  };
}

// Email validation
function isValidEmail(email) {
  // RFC 5322 Official Standard (simplified)
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);
}

// Requirement component moved outside render for performance
const Requirement = ({ met, children }) => (
  <li style={{ color: met ? '#22c55e' : '#fecaca', fontWeight: 500 }}>
    {met ? <FaCheckCircle color="#22c55e" style={{ marginRight: 6 }} /> : <FaTimesCircle color="#ef4444" style={{ marginRight: 6 }} />} 
    <span style={{ color: met ? '#bbf7d0' : '#fecaca' }}>{children}</span>
  </li>
);

export default function Signup() {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [status, setStatus] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  // Compute password requirement results
  const requirements = checkPasswordRequirements(password, email);
  // Must meet all Cognito requirements and no forbidden char
  const passwordValid = Object.values(requirements).every(Boolean);
  const emailValid = isValidEmail(email);



  const handleSignup = async () => {
    if (!firstName || !email || !password) {
      setStatus("❌ All fields are required.");
      return;
    }
    if (!emailValid) {
      setStatus("❌ Please enter a valid email address.");
      return;
    }
    if (!passwordValid) {
      setStatus("❌ Password does not meet the policy.");
      return;
    }
    setIsLoading(true);
    const signupToken = uuidv4();
    const csrfToken = uuidv4();
    try {
      const response = await fetch("https://3croovkdaa.execute-api.us-east-1.amazonaws.com/store-signup", {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken
        },
        body: JSON.stringify({
          token: signupToken,
          name: firstName,
          email,
          password,
          csrfToken
        }),
      });
      if (!response.ok) {
        let errorMessage = "Failed to stage signup data";
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch {
          // Use default message if response parsing fails
        }
        throw new Error(errorMessage);
      }
      sessionStorage.setItem("signupEmail", email);
      sessionStorage.setItem("signupToken", signupToken);
      navigate("/subscribe");
    } catch (err) {
      setStatus(`❌ ${err.message}`);
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div style={{ padding: "4rem", textAlign: "center" }}>
        <Circumpunct />
        <p style={{ marginTop: "1rem" }}>Preparing your signup...</p>
      </div>
    );
  }

  return (
    <Page>
      <Card>
        <Heading>Sign up for Skroop</Heading>
        <form
          onSubmit={e => {
            e.preventDefault();
            handleSignup();
          }}
        >
          <InputGroup>
            <Input
              type="text"
              name="given-name"
              autoComplete="given-name"
              placeholder="First name"
              value={firstName}
              onChange={e => setFirstName(e.target.value)}
            />
          </InputGroup>
          <InputGroup>
            <Input
              type="email"
              name="email"
              autoComplete="email"
              placeholder="Email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              style={{ borderColor: email.length === 0 ? "#ccc" : (emailValid ? "#22c55e" : "#ef4444") }}
            />
            {email.length > 0 && !emailValid && (
              <div style={{ color: "#ef4444", fontSize: "0.92em", marginTop: 2 }}>
                Please enter a valid email address.
              </div>
            )}
          </InputGroup>
          <InputGroup>
            <PasswordWrapper>
              <PasswordInput
                as={Input} // use base styles
                type={showPassword ? "text" : "password"}
                name="new-password"
                autoComplete="new-password"
                placeholder="Password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                valid={passwordValid}
              />
              <PasswordToggleIcon onClick={() => setShowPassword(prev => !prev)}>
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </PasswordToggleIcon>
            </PasswordWrapper>
          </InputGroup>
          <PasswordPolicy>
            <div style={{ display: "flex", alignItems: "center", marginBottom: 6 }}>
              <span style={{ fontSize: "0.97em", fontWeight: 500 }}>
                Your password must contain:
              </span>
              <TooltipWrapper>
                <Info size={16} style={{ color: "#fde68a", marginLeft: 4, cursor: "pointer" }} />
                <TooltipText>
                  These are the permitted special characters.<br />Spaces are not allowed.<br />
                  <span style={{ color: "#fde68a", wordBreak: "break-all" }}>{cognitoSpecialChars}</span>
                </TooltipText>
              </TooltipWrapper>
            </div>
            <ul>
              <Requirement met={requirements.length}>At least 8 characters</Requirement>
              <Requirement met={requirements.upper}>One uppercase letter</Requirement>
              <Requirement met={requirements.lower}>One lowercase letter</Requirement>
              <Requirement met={requirements.number}>One number</Requirement>
              <Requirement met={requirements.special}>One permitted special character</Requirement>
              <Requirement met={requirements.noNonPermitted}>No spaces or forbidden characters</Requirement>
              <Requirement met={requirements.notEmail}>Cannot contain your email username</Requirement>
            </ul>
          </PasswordPolicy>
          <Button type="submit" disabled={!passwordValid || !firstName || !emailValid}>Continue with Email</Button>
        </form>
        {status && <StatusMessage success={status.startsWith("✅") ? "true" : undefined}>{status}</StatusMessage>}
      </Card>
    </Page>
  );
}
